import {
  INITIAL_F2F_OPTION_SET,
  INITIAL_F2F_STATEMENT_CATALOG,
} from "@/pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/note-forms/initial-f2f";
import {
  NOTE_PREVIEW_LEADING_NOTE_MARKER,
  NOTE_PREVIEW_NOTE_MARKER,
  NOTE_PREVIEW_TRAILING_SEPARATORS,
} from "./note-preview-constant";
import { splitOnCommas } from "./note-preview-split-utils";

interface StatementMatch {
  start: number;
  end: number;
}

const LOWER_CATALOG = INITIAL_F2F_STATEMENT_CATALOG.map((statement) =>
  statement.toLowerCase()
);

/** Non-overlapping catalog statements found in the text, in reading order; longest statements win. */
const findStatements = (text: string): StatementMatch[] => {
  const lower = text.toLowerCase();
  const matches: StatementMatch[] = [];
  LOWER_CATALOG.forEach((statement) => {
    let from = 0;
    for (;;) {
      const start = lower.indexOf(statement, from);
      if (start === -1) break;
      const end = start + statement.length;
      const overlaps = matches.some((m) => start < m.end && end > m.start);
      if (!overlaps) matches.push({ start, end });
      from = end;
    }
  });
  return matches.sort((a, b) => a.start - b.start);
};

const trimSeparators = (text: string) =>
  text.replace(NOTE_PREVIEW_TRAILING_SEPARATORS, "").replace(/^[\s,.]+/, "");

const stripBrackets = (text: string) => text.replace(/^\((.*)\)$/, "$1").trim();

/** Loose text that is not one of the form's options is the provider's typed note; it gets the "Note:" label. */
const labelLoose = (item: string): string => {
  const bare = stripBrackets(item);
  if (NOTE_PREVIEW_LEADING_NOTE_MARKER.test(bare)) return bare;
  return INITIAL_F2F_OPTION_SET.has(bare.toLowerCase())
    ? bare
    : `${NOTE_PREVIEW_NOTE_MARKER}${bare}`;
};

/** Text that opens with one of the form's options, e.g. "no impairment", rather than a bare yes/no answer. */
export const startsWithFormOption = (text: string): boolean => {
  const lower = text.trim().toLowerCase();
  return [...INITIAL_F2F_OPTION_SET].some(
    (option) =>
      lower === option ||
      [",", ".", " ("].some((next) => lower.startsWith(`${option}${next}`))
  );
};

/**
 * Cuts a comma-joined answer back into one bullet per ticked statement using
 * the form's own option texts, so commas inside a statement never split it.
 * Text between statements (short options such as "refer for LVN") is comma
 * split. Returns undefined when no catalog statement is present.
 */
export const splitByStatementCatalog = (text: string): string[] | undefined => {
  const matches = findStatements(text);
  if (!matches.length) return undefined;
  const items: string[] = [];
  // Bracketed text is what was typed in the previous option's box; it stays on that bullet.
  const pushLoose = (loose: string) =>
    splitOnCommas(trimSeparators(loose)).forEach((item) => {
      if (item.startsWith("(") && items.length)
        items[items.length - 1] += ` ${item}`;
      else items.push(labelLoose(item));
    });
  pushLoose(text.slice(0, matches[0].start));
  matches.forEach((match, index) => {
    items.push(trimSeparators(text.slice(match.start, match.end)));
    const next = matches[index + 1];
    pushLoose(text.slice(match.end, next ? next.start : undefined));
  });
  return items.filter(Boolean);
};
